import { z } from "zod";
import { IdeationMapEdgeSchema, IdeationNodeContextSchema } from "./ideation-maps.js";
/**
 * Map history — git-object-model history layer for Ideation Maps.
 *
 * Each logical write auto-commits a content-addressed snapshot: member node
 * bodies are stored once per unique content in `ideation_content_blobs`; a
 * commit row holds the full tree manifest inline (map structure +
 * {nodeId → blobHash}) so a historical render costs one row read + one IN
 * query for blobs, with no replay.
 *
 * Design: docs/enhancements/ideation-map-history-2026-06.md
 * ADR-0074: docs/adr/0074-ideation-map-history-git-object-model.md
 */
// ─── Node snapshot (stored in content blobs) ──────────────────────────────
/** Full node state captured at commit time. Stored as one blob per unique
 *  content hash — an unchanged node shared across many commits costs one row. */
export const NodeSnapshotSchema = z.object({
    label: z.string(),
    kind: z.string(),
    aliases: z.array(z.string()),
    context: IdeationNodeContextSchema,
    weight: z.number().nullable(),
    status: z.string(),
});
// ─── History tree (inline on each commit) ─────────────────────────────────
/** The snapshot manifest stored inline on a commit row. Contains:
 *  - `mapStructure`: all map-level fields captured at commit time.
 *  - `members`: ordered array of {nodeId, blobHash} — each blobHash points to
 *    a row in `ideation_content_blobs` holding the NodeSnapshot for that node. */
export const HistoryTreeSchema = z.object({
    mapStructure: z.object({
        title: z.string(),
        kind: z.string(),
        childMaps: z.array(z.string()),
        flowEdges: z.array(IdeationMapEdgeSchema),
        order: z.record(z.string(), z.number()).nullable(),
        layout: z.record(z.string(), z.object({ x: z.number(), y: z.number() })),
        membershipRule: z.string().nullable(),
        visibility: z.string(),
    }),
    members: z.array(z.object({ nodeId: z.string(), blobHash: z.string() })),
});
// ─── Commit ────────────────────────────────────────────────────────────────
export const COMMIT_SOURCES = ["manual", "extraction", "restore", "chat-foldback"];
export const IdeationMapCommitSchema = z.object({
    id: z.string().uuid(),
    mapId: z.string().uuid(),
    /** Always 'main' in v1. `branch` + `parentCommitId` are reserved so future
     *  branching is additive with no schema migration. */
    branch: z.string(),
    parentCommitId: z.string().uuid().nullable(),
    /** Monotonically increasing per map (1-based). Safe for display as "#N". */
    seq: z.number().int(),
    tree: HistoryTreeSchema,
    /** sha256(stableStringify(tree)) — no-op guard: skip commit when this matches
     *  the parent's treeHash (identical state = no write). */
    treeHash: z.string(),
    /** Optional human-readable label (e.g. "Before restructure"). */
    label: z.string().nullable(),
    source: z.enum(COMMIT_SOURCES),
    createdBy: z.string(),
    createdAt: z.string(), // loose ISO timestamp per response-schema convention
});
// ─── List ──────────────────────────────────────────────────────────────────
export const IdeationMapCommitListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(50),
    /** ISO timestamp cursor — return commits with createdAt < before (pagination). */
    before: z.string().optional(),
});
export const IdeationMapCommitListResponseSchema = z.object({
    commits: z.array(IdeationMapCommitSchema),
    hasMore: z.boolean(),
});
// ─── Commit view (hydrated for rendering) ─────────────────────────────────
/** A commit hydrated with full node snapshots — one query to render any
 *  historical state of the map without replaying the mutation log. */
export const IdeationCommitViewNodeSchema = NodeSnapshotSchema.extend({
    id: z.string().uuid(),
});
export const IdeationCommitViewSchema = z.object({
    commit: IdeationMapCommitSchema,
    nodes: z.array(IdeationCommitViewNodeSchema),
});
// ─── Restore ──────────────────────────────────────────────────────────────
/** Warning computed before restore: nodes in this commit that also appear in
 *  other non-archived maps. Restoring node content affects those maps too. */
export const IdeationRestoreWarningSchema = z.object({
    crossMapNodes: z.array(z.object({
        nodeId: z.string().uuid(),
        label: z.string(),
        otherMaps: z.array(z.object({ id: z.string().uuid(), title: z.string() })),
    })),
});
export const IdeationRestoreResultSchema = z.object({
    /** The new restore-commit id (a new commit is always created on restore). */
    commitId: z.string().uuid(),
    seq: z.number().int(),
});
//# sourceMappingURL=ideation-history.js.map